import React, { useState, useEffect } from 'react';
import studentLessonService from '../../services/student/studentLessonService';

export default function StudentDashboardContent({ studentData, setCurrentPage }) {
  const [stats, setStats] = useState({
    totalLessons: 0,
    todayLessons: 0,
    monthlyLessons: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Dersleri getir
      const lessonsData = await studentLessonService.getMyLessons();
      
      // Bugünün tarihini al
      const today = new Date().toISOString().split('T')[0];
      
      // Bugünkü dersleri filtrele
      const todayLessons = lessonsData.lessons.filter(lesson => {
        const lessonDate = new Date(lesson.baslangic_tarihi).toISOString().split('T')[0];
        return lessonDate === today;
      });
      
      // Bu ayın derslerini filtrele
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      const monthlyLessons = lessonsData.lessons.filter(lesson => {
        const lessonDate = new Date(lesson.baslangic_tarihi);
        return lessonDate.getMonth() === currentMonth && lessonDate.getFullYear() === currentYear;
      });
      
      setStats({
        totalLessons: lessonsData.count,
        todayLessons: todayLessons.length,
        monthlyLessons: monthlyLessons.length
      });
      
    } catch (error) {
      console.error('Dashboard stats yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Kalan bakiye renk ve icon belirleme
  const getBalanceStyle = () => {
    const balance = studentData?.kalan_bakiye || 0;
    if (balance > 0) {
      // Pozitif bakiye = Fazla ödeme yapmış, alacağı var = YEŞİL
      return { 
        bgColor: 'bg-green-500', 
        textColor: 'text-white',
        icon: '💰',
        label: 'Kalan Bakiye'
      };
    } else if (balance < 0) {
      // Negatif bakiye = Borcu var, ödemesi gereken = KIRMIZI
      return { 
        bgColor: 'bg-red-500', 
        textColor: 'text-white',
        icon: '💸',
        label: 'Kalan Bakiye'
      };
    } else {
      return { 
        bgColor: 'bg-blue-500', 
        textColor: 'text-white',
        icon: '✅',
        label: 'Kalan Bakiye'
      };
    }
  };

  const balanceStyle = getBalanceStyle();

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-8">Hoş Geldiniz, {studentData?.name}</h1>
      
      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-green-500 text-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Toplam Ders</p>
              <p className="text-3xl font-bold">
                {loading ? '...' : stats.totalLessons}
              </p>
            </div>
            <span className="text-4xl opacity-80">📚</span>
          </div>
        </div>
        
        <div className="bg-purple-500 text-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Bugünkü Ders</p>
              <p className="text-3xl font-bold">
                {loading ? '...' : stats.todayLessons}
              </p>
            </div>
            <span className="text-4xl opacity-80">📅</span>
          </div>
        </div>
        
        <div className="bg-orange-500 text-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Bu Ay</p>
              <p className="text-3xl font-bold">
                {loading ? '...' : stats.monthlyLessons}
              </p>
            </div>
            <span className="text-4xl opacity-80">📊</span>
          </div>
        </div>

        {/* Kalan Bakiye Kartı */}
        <div className={`${balanceStyle.bgColor} ${balanceStyle.textColor} p-6 rounded-lg shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">{balanceStyle.label}</p>
              <p className="text-2xl font-bold">
                {studentData?.kalan_bakiye !== undefined && studentData?.kalan_bakiye !== null ? (
                  <>
                    {studentData.kalan_bakiye > 0 ? '+' : ''}{studentData.kalan_bakiye.toFixed(2)} ₺
                  </>
                ) : (
                  '0.00 ₺'
                )}
              </p>
              {studentData?.kalan_bakiye < 0 && (
                <p className="text-xs opacity-80 mt-1">Ödemeniz gereken</p>
              )}
              {studentData?.kalan_bakiye === 0 && (
                <p className="text-xs opacity-80 mt-1">Bakiyeniz temiz</p>
              )}
            </div>
            <span className="text-4xl opacity-80">{balanceStyle.icon}</span>
          </div>
        </div>
      </div>
      
      {/* Kişisel Bilgiler Kartı */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Kişisel Bilgilerim</h2>
          <button 
            onClick={() => setCurrentPage('profile')}
            className="text-sm text-green-600 hover:text-green-800 flex items-center"
          >
            Profili Görüntüle
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">Ad Soyad</p>
            <p className="font-medium text-gray-900">{studentData?.name || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">E-posta</p>
            <p className="font-medium text-gray-900">{studentData?.email || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">Veli</p>
            <p className="font-medium text-gray-900">{studentData?.parent_name || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">Veli Telefon</p>
            <p className="font-medium text-gray-900">{studentData?.parent_phone || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">Şehir</p>
            <p className="font-medium text-gray-900">{studentData?.city || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">Sınıf</p>
            <p className="font-medium text-gray-900">{studentData?.sinif || '-'}</p>
          </div>
        </div>
      </div>

      {/* Hızlı Erişim Butonları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <button
          onClick={() => setCurrentPage('lessons')}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-center group"
        >
          <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">📚</span>
          <span className="text-base font-medium">Derslerim</span>
          <span className="text-xs text-gray-500 block mt-1">
            {loading ? '...' : `${stats.totalLessons} ders`}
          </span>
        </button>
        
        <button
          onClick={() => setCurrentPage('payments')}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-center group"
        >
          <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">💳</span>
          <span className="text-base font-medium">Ödemelerim</span>
          <span className="text-xs text-gray-500 block mt-1">
            Geçmişi görüntüle
          </span>
        </button>
        <button
          onClick={() => setCurrentPage('profile')}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-center group"
        >
          <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">👤</span>
          <span className="text-base font-medium">Profilim</span>
        </button>
        <button
          onClick={() => setCurrentPage('settings')}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-center group"
        >
          <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">⚙️</span>
          <span className="text-base font-medium">Ayarlar</span>
        </button>
      </div>
      
      {/* Genel Bilgi Kartı */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Hesap Bilgileri</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border-l-4 border-green-500 pl-4">
            <p className="text-sm text-gray-600">Aldığım Dersler</p>
            <p className="text-2xl font-bold text-green-600">
              {loading ? '...' : stats.totalLessons}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Toplam ders sayısı
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-sm text-gray-600">Kayıt Durumu</p>
            <p className="text-lg font-medium text-blue-600">
              {studentData?.status === 1 ? 'Aktif' : 'Pasif'}
            </p>
          </div>
          <div className={`border-l-4 ${studentData?.kalan_bakiye > 0 ? 'border-green-500' : studentData?.kalan_bakiye < 0 ? 'border-red-500' : 'border-blue-500'} pl-4`}>
            <p className="text-sm text-gray-600">Kalan Bakiye</p>
            <p className={`text-2xl font-bold ${studentData?.kalan_bakiye > 0 ? 'text-green-600' : studentData?.kalan_bakiye < 0 ? 'text-red-600' : 'text-blue-600'}`}>
              {studentData?.kalan_bakiye !== undefined && studentData?.kalan_bakiye !== null ? (
                <>
                  {studentData.kalan_bakiye > 0 ? '+' : ''}{studentData.kalan_bakiye.toFixed(2)} ₺
                </>
              ) : (
                '0.00 ₺'
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {studentData?.kalan_bakiye > 0 ? 'Alacak' : studentData?.kalan_bakiye < 0 ? 'Borç' : 'Temiz'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}